import { DOC_TYPE_LABELS } from '@/constants';
import {
  DocType,
  SearchScope,
  SurveyPageName,
  UserDocumentFeatureName,
} from '@/types';
import { featureFlags } from '@/featureFlags';

// 要約v1で使用する、検索対象の絞り込み項目
export const TARGET_SOURCES = (
  enableTechnicalLiterature: boolean,
  enableUserDocument: boolean,
) =>
  [
    {
      section: '公開情報',
      options: [
        {
          label: 'ニュース',
          value: 'article',
        },
        ...(enableTechnicalLiterature
          ? [
              {
                label: '論文',
                value: 'research_paper',
              },
              {
                label: '特許',
                value: 'patent',
              },
            ]
          : []),
        {
          label: 'レポート',
          value: 'report',
        },
      ],
    },
    ...(enableUserDocument
      ? [
          {
            section: '社内情報',
            options: [
              {
                label: 'すべて',
                value: 'user_document',
              },
            ],
          },
        ]
      : []),
  ] as Array<{
    section: string;
    options: Array<{
      label: (typeof DOC_TYPE_LABELS)[keyof typeof DOC_TYPE_LABELS];
      value: keyof typeof DOC_TYPE_LABELS;
    }>;
  }>;

// TARGET_SOURCESのoptionsの全てのvalue
export const ALL_DOC_TYPES = (
  enableTechnicalLiterature: boolean,
  enableUserDocument: boolean,
) =>
  TARGET_SOURCES(enableTechnicalLiterature, enableUserDocument).flatMap(
    source => source.options.map(option => option.value),
  ) as Array<DocType | 'user_document'>;

// マークした情報の要約で使用する、検索対象の絞り込み項目
export const TARGET_SOURCE_LABELS = (
  searchScope: SearchScope,
  enableTechnicalLiterature: boolean,
  enableUserDocument: boolean,
) =>
  [
    {
      label: 'ニュース',
      value: 'article',
    },
    ...(searchScope === 'user_marks' || searchScope === 'group_marks'
      ? [{ label: 'レポート', value: 'report' }]
      : []),
    ...(enableTechnicalLiterature
      ? [
          {
            label: '論文',
            value: 'research_paper',
          },
          {
            label: '特許',
            value: 'patent',
          },
        ]
      : []),
    ...(searchScope === 'all' || searchScope === 'specified_files'
      ? [
          {
            label: 'レポート',
            value: 'report',
          },
        ]
      : []),
    ...(enableUserDocument &&
    (searchScope === 'all' || searchScope === 'specified_files')
      ? [
          {
            label: '社内情報',
            value: 'user_document',
          },
        ]
      : []),
  ] as Array<{
    label: (typeof DOC_TYPE_LABELS)[keyof typeof DOC_TYPE_LABELS];
    value: keyof typeof DOC_TYPE_LABELS;
  }>;

export const DOC_TYPES_FOR_SCOPE = (
  searchScope: SearchScope,
  enableTechnicalLiterature: boolean,
  enableUserDocument: boolean,
) =>
  TARGET_SOURCE_LABELS(
    searchScope,
    enableTechnicalLiterature,
    enableUserDocument,
  ).map(option => option.value) as Array<DocType | 'user_document'>;

// surveyのstreamingデータのtype
export const STREAMING_DATA_TYPES = {
  PROGRESS: 'progress', // 要約の進捗
  DEEP_MODE_PROCESS: 'deep_mode_process', // ディープリサーチのプロセス (推論結果と検索クエリ)
  SOURCE: 'source', // 要約に使用したソース (検索にヒットしたドキュメント)
  NO_HIT_SOURCES: 'no_hit_sources', // 検索で1件もドキュメントがヒットしなかった時に返ってくる
  SOURCES_DONE: 'sources_done', // 要約に使用するソースが全て返却し終わったら返ってくる
  REASONING_START: 'reasoning_start', // Chain Of Thought の生成開始
  REASONING_TOKEN: 'reasoning_token', // Chain Of Thought の生成内容
  REASONING_END: 'reasoning_end', // Chain Of Thought の生成完了
  TOKEN: 'token', // 要約本文
  RELATED_QUESTIONS: 'related_questions', // 関連質問
  SURVEY_DONE: 'survey_done', // surveyのAPIが完了した時に返ってくる
  ERROR: 'error', // モデルが要約を生成する過程のどこかでエラーが発生した時に返ってくる
  META: 'meta', // メタ情報
} as const;

// 計測で使用するsurveyの機能やページ名
export const SURVEY_FEATURE_NAME: Extract<UserDocumentFeatureName, 'survey'> =
  'survey' as const;
export const SURVEY_HISTORIES_PAGE_NAME: Extract<SurveyPageName, 'surveys'> =
  'surveys' as const;
export const SURVEY_SEARCH_PAGE_NAME: Extract<
  SurveyPageName,
  'surveys_search'
> = 'surveys_search' as const;
export const SURVEY_TOP_PAGE_NAME: Extract<SurveyPageName, 'survey_top'> =
  'survey_top' as const;
export const SHARE_SURVEY_PAGE_NAME: Extract<SurveyPageName, 'share_surveys'> =
  'share_surveys' as const;
export const SHARE_SURVEYS_SEARCH_SHARED_BY_OTHERS_PAGE_NAME: Extract<
  SurveyPageName,
  'share_surveys_search_shared_by_others'
> = 'share_surveys_search_shared_by_others' as const;
export const SHARE_SURVEYS_SEARCH_SHARED_BY_ME_PAGE_NAME: Extract<
  SurveyPageName,
  'share_surveys_search_shared_by_me'
> = 'share_surveys_search_shared_by_me' as const;
export const SHARE_SURVEYS_SEARCH_NOT_SHARED_PAGE_NAME: Extract<
  SurveyPageName,
  'share_surveys_search_shared_not_shared'
> = 'share_surveys_search_shared_not_shared' as const;

export const SURVEY_ERROR_MESSAGES: Record<string, string> = {
  no_user_marks:
    'マークされた情報が存在しないため回答を生成できませんでした。記事にマークしてから再度お試しください。',
  deep_research_concurrency_limit:
    'ディープリサーチ実行中のため現在新規生成ができません。実行が完了した後に再度生成してください。',
  timeout:
    '処理に時間がかかっているため画面の表示を停止しています。ブラウザをリロードしてください。',
  default:
    '申し訳ありませんが、現在こちらの回答を生成することができません。後ほどお試しください。',
};

// 検索から要約Top画面に遷移した際に、入力欄に自動挿入するクエリ
export const SEARCH_TO_SURVEY_FILE_SPECIFICATION_QUERY =
  'このファイルを要約してください';

// マイページの「マークした情報から要約生成」ボタンで要約Top画面に遷移した際に、入力欄に自動挿入するクエリ
export const SEARCH_TO_SURVEY_USER_MARKS_QUERY =
  'マイページの情報を要約してください';

// グループの「グループマークした情報から質問」ボタンで要約Top画面に遷移した際に、入力欄に自動挿入するクエリ
export const SEARCH_TO_SURVEY_GROUP_MARKS_QUERY =
  'グループの情報を要約してください';

export const DEFAULT_SURVEY_PROGRESS_MESSAGE = '回答形式を考えています';

// 1スレッドで生成できる要約の上限数
export const SESSION_LIMIT_COUNT = 50;

// 要約クエリの文字数上限
export const SURVEY_QUESTION_MAX_LENGTH =
  featureFlags.ANDEV_10336_RAISE_SURVEY_PROMPT_LIMIT ? 10000 : 1000;

// 検索結果の1ページあたりの取得件数
export const SURVEY_SEARCH_PAGE_LIMIT = 20;

// ディープリサーチの最大同時実行数
export const DEEP_MODE_MAX_CONCURRENCY = 1;

export const DEEP_MODE_MAX_CONCURRENCY_MESSAGE =
  'ディープリサーチ生成完了までお待ちください（通常要約は生成可能です）';

// surveyの実行状態をポーリングする間隔
export const SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_MS = 15000; // 通常時のポーリング間隔（15秒）
export const SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_INITIAL_MS = 5000; // 実行直後のポーリング間隔（5秒）
export const SURVEY_EXECUTION_STATUS_POLLING_INITIAL_DURATION_MS = 60000; // 初期間隔を使用する期間（1分間）
export const MAX_POLLING_EMPTY_RESULT_COUNT = 3; // 連続でsurvey実行状態が取得できなければポーリング停止
